"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HttpClient = exports.isHttpError = exports.HttpError = void 0;
const _ = require("lodash");
const events_1 = require("events");
const axios_1 = require("axios");
const url_1 = require("url");
const https_1 = require("https");
const http_1 = require("http");
/**
 * Represents the http response code and message.
 */
class HttpError {
    constructor(message, code = 500) {
        this.code = code;
        this.message = message || "internal error";
    }
}
exports.HttpError = HttpError;
/**
 * Check if `h` is `HttpError` or not.
 *
 * @param h
 */
function isHttpError(h) {
    return !!(h && h.code);
}
exports.isHttpError = isHttpError;
/**
 * Compared to the axios library, which throws an error with non-200 status,
 * this client returns [[HttpLog]] data, instead.
 * In addition, this client provides logging and retry features.
 * With the provided `logFn` function, the user can have a chance to record the http request and response
 * when the HTTP transaction is finished. The `retryFn` in the option
 * can be used to re-execute the failed http transaction with the same or modified request.
 * The [[HttpClientOption.retryFn]] takes `config`, `log` and `retry` parameter to make a decision for retry.
 * The `config` parameter is mutable and can be changed for a subsequent request.
 */
class HttpClient extends events_1.EventEmitter {
    constructor(axiosOpts, opts = {}) {
        super();
        if (!axiosOpts.httpAgent) {
            axiosOpts.httpAgent = new http_1.Agent({ keepAlive: true });
        }
        if (!axiosOpts.httpsAgent) {
            axiosOpts.httpsAgent = new https_1.Agent({ keepAlive: true, maxCachedSessions: 60 * 10 });
        }
        this._axiosConfig = axiosOpts;
        this._httpClient = axios_1.default.create(this._axiosConfig);
        this.setOptions(opts);
    }
    setOptions(opts) {
        this._opts = opts;
        if (!_.isSafeInteger(this._opts.maxRetry)) {
            this._opts.maxRetry = 10;
        }
        if (!_.isSafeInteger(this._opts.retryIntervalMs)) {
            this._opts.retryIntervalMs = 1000;
        }
        this._assignId = this._opts.assignId;
    }
    /**
     * Returns an axios instance that copies of this instance config.
     */
    axios(baseURL) {
        if (!baseURL) {
            if (this._axiosConfig.baseURL) {
                let baseurl = url_1.parse(this._axiosConfig.baseURL);
                baseURL = this._axiosConfig.baseURL.replace(baseurl.path, "");
            }
        }
        return axios_1.default.create(_.assign({}, this._axiosConfig, { baseURL: baseURL }));
    }
    /**
     * If the httpLOg is an error returns `code` and `message.
     *
     * @param httpLog
     */
    getHttpReturn(httpLog) {
        if (httpLog.status !== 200) {
            if (httpLog.data) {
                return httpLog.data;
            }
            return new HttpError(httpLog.statusText || httpLog.errorMessage, httpLog.status || (httpLog.errorName && 500));
        }
        return httpLog.data || {};
    }
    async get(url, config) {
        const httpLog = await this.$get(url, config);
        return this.getHttpReturn(httpLog);
    }
    async post(url, body, config) {
        const httpLog = await this.$post(url, body, config);
        return this.getHttpReturn(httpLog);
    }
    $get(url, config) {
        return this.request(Object.assign(config || {}, { method: "GET", url }));
    }
    $post(url, body, config) {
        return this.request(Object.assign(config || {}, { method: "POST", url, data: body }));
    }
    /**
     * Wraps the request and returns [[HttpLog]]
     *
     * @param config
     */
    async request(config, retry = 0) {
        return new Promise(async (resolve) => {
            var _a;
            const { url, method, data } = config;
            let httpLog = {
                url: url,
                method: method,
                baseURL: this._axiosConfig.baseURL,
                postBody: data
            };
            if (this._assignId) {
                httpLog.uuid = this._assignId(httpLog);
            }
            // debug("http request %o", config);
            let resp;
            let startTime = Date.now();
            try {
                httpLog.requestAt = Date.now();
                resp = await this._httpClient.request(config);
                httpLog.responseAt = Date.now();
                httpLog.elapsedTimeMS = httpLog.responseAt - httpLog.requestAt;
                httpLog.status = resp.status;
                httpLog.statusText = resp.statusText;
                httpLog.data = resp.data;
                this.emit("ok", httpLog, config, this);
            }
            catch (err) {
                httpLog.isClientError = err.isAxiosError;
                httpLog.responseAt = Date.now();
                httpLog.elapsedTimeMS = httpLog.responseAt - httpLog.requestAt;
                if (err.response) {
                    httpLog.status = err.response.status;
                    httpLog.statusText = err.response.statusText;
                    httpLog.data = err.response.data;
                }
                else {
                    httpLog.status = 500;
                    httpLog.statusText = err.message;
                    httpLog.errorName = err.name;
                    httpLog.errorMessage = err.message;
                }
                this.emit("fail", err, httpLog, config, this);
            }
            finally {
                const opts = this._opts;
                if (opts.logFn) {
                    try {
                        const maybePromise = opts.logFn(httpLog);
                        if (maybePromise && maybePromise.then) {
                            maybePromise.then(promiseReturn => {
                                this.emit("log-ok", httpLog, this, promiseReturn);
                            });
                        }
                        else {
                            this.emit("log-ok", httpLog, this);
                        }
                    }
                    catch (err) {
                        this.emit("log-fail", err, httpLog, this);
                    }
                }
                const retryFn = (_a = config.retryFn) !== null && _a !== void 0 ? _a : opts.retryFn;
                if (++retry <= opts.maxRetry && retryFn) {
                    try {
                        httpLog.retry = retry;
                        const interval = retryFn(config, httpLog, retry);
                        if (interval > 0) {
                            const timer = setTimeout(() => {
                                this.emit("retry", httpLog, config, retry, timer, this);
                                this.request(config, retry).then(resolve);
                            }, interval);
                            return;
                        }
                    }
                    catch (err) {
                        this.emit("retry-error", err, httpLog, config, retry, this);
                    }
                }
            }
            resolve(httpLog);
        });
    }
    destroy() {
        var _a, _b;
        (_a = this._axiosConfig.httpAgent) === null || _a === void 0 ? void 0 : _a.destroy();
        (_b = this._axiosConfig.httpsAgent) === null || _b === void 0 ? void 0 : _b.destroy();
    }
}
exports.HttpClient = HttpClient;
//# sourceMappingURL=httpClient.js.map